import React, { useState, useEffect, Suspense, lazy } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider } from '@react-oauth/google';

// Layout Components
import Header from "./components/layout/Header";

// Page Components
// MapDetailsPage is the landing route (/, /map, listing deep links) so it
// loads eagerly; everything else is code-split out of the main bundle since
// most visitors never touch admin/auth/gallery on their first load.
import MapDetailsPage from "./pages/MapDetailsPage";

// Custom Hooks
import { useAuth, useLanguage, useFilters, useSearch, useViewMode } from "./hooks";

// Services
import { tagsService } from "./services/api";

const AdminPanelPage = lazy(() => import("./pages/AdminPanelPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const AddLandPage = lazy(() => import("./pages/AddLandPage"));
const AddTagPage = lazy(() => import("./pages/AddTagPage"));
const AddUserPage = lazy(() => import("./pages/AddUserPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const DeleteAccountPage = lazy(() => import("./pages/DeleteAccountPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

// Google Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * Main App Component
 * - Manages global state using custom hooks
 * - Provides routing structure
 * - Renders layout and page components
 */
function App() {
  // Custom hooks for state management
  const { isAuthenticated, loading, handleLogin, handleLogout, markAuthenticated } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const { filterOpen, activeFilters, toggleFilter, handleFilterToggle, handleActiveFiltersChange } = useFilters();
  const { searchQuery, handleSearchChange } = useSearch();
  const { viewMode, setViewMode } = useViewMode();
  
  // Tags state for multi-select dropdown
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [lands, setLands] = useState([]);
  const [showSavedOnly, setShowSavedOnly] = useState(false);
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  
  const navigate = useNavigate();

  // Fetch tags on mount
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const fetchedTags = await tagsService.getAllTags();
        setTags(fetchedTags);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, []);

  // Debug: Log lands changes
  useEffect(() => {
    console.log('App.js - Lands updated:', lands.length);
    if (lands.length > 0) {
      console.log('App.js - Sample land:', lands[0]);
    }
  }, [lands]);

  const handleTagsChange = (newSelectedTags) => {
    setSelectedTags(newSelectedTags);
  };

  const handleLogoutAndNavigate = () => {
    handleLogout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-cream">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-brand font-cairo">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div className="flex flex-col h-screen bg-[#f8f9fa] overflow-hidden">
        {/* Header - Consistent across all pages */}
        <Header 
          isAuthenticated={isAuthenticated}
          language={language}
          onLogout={handleLogoutAndNavigate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onFilterClick={toggleFilter}
          activeFilters={activeFilters}
          tags={tags}
          selectedTags={selectedTags}
          onTagsChange={handleTagsChange}
          lands={lands}
          showSavedOnly={showSavedOnly}
          onSavedToggle={setShowSavedOnly}
          priceMin={priceRange.min}
          priceMax={priceRange.max}
          onPriceRangeChange={setPriceRange}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-auto">
        <Suspense fallback={
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
          </div>
        }>
        <Routes>
          {/* Map View - Public */}
          <Route path="/" element={
            <MapDetailsPage 
              language={language} 
              onFilterToggle={handleFilterToggle}
              onActiveFiltersChange={handleActiveFiltersChange}
              filterOpen={filterOpen}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              viewMode={viewMode}
              selectedTags={selectedTags}
              onTagsChange={handleTagsChange}
              isAuthenticated={isAuthenticated}
              onLandsChange={setLands}
              showSavedOnly={showSavedOnly}
              priceMin={priceRange.min}
              priceMax={priceRange.max}
            />
          } />
          
          {/* Map Route - Alias for root */}
          <Route path="/map" element={
            <MapDetailsPage 
              language={language} 
              onFilterToggle={handleFilterToggle}
              onActiveFiltersChange={handleActiveFiltersChange}
              filterOpen={filterOpen}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              viewMode={viewMode}
              selectedTags={selectedTags}
              onTagsChange={handleTagsChange}
              isAuthenticated={isAuthenticated}
              onLandsChange={setLands}
              showSavedOnly={showSavedOnly}
              priceMin={priceRange.min}
              priceMax={priceRange.max}
            />
          } />
          
          {/* Land Detail Deep Link - Public (e.g. /459/land-title) */}
          <Route path="/:landId/:titleSlug" element={
            <MapDetailsPage
              language={language}
              onFilterToggle={handleFilterToggle}
              onActiveFiltersChange={handleActiveFiltersChange}
              filterOpen={filterOpen}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              viewMode={viewMode}
              selectedTags={selectedTags}
              onTagsChange={handleTagsChange}
              isAuthenticated={isAuthenticated}
              onLandsChange={setLands}
              showSavedOnly={showSavedOnly}
              priceMin={priceRange.min}
              priceMax={priceRange.max}
            />
          } />

          {/* Gallery - Public */}
          <Route path="/gallery/:landId" element={
            <GalleryPage language={language} />
          } />

          {/* Privacy Policy - Public, not linked anywhere in the UI (direct URL only) */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />

          {/* Consumer Profile - Public visitor account (Google Sign-In, non-staff) */}
          <Route path="/profile" element={<ProfilePage language={language} />} />

          {/* Tag Filter Deep Link - Public (e.g. /صباح-الأحمد-البحرية-المرحلة-الأولى) */}
          <Route path="/:tagSlug" element={
            <MapDetailsPage
              language={language}
              onFilterToggle={handleFilterToggle}
              onActiveFiltersChange={handleActiveFiltersChange}
              filterOpen={filterOpen}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              viewMode={viewMode}
              selectedTags={selectedTags}
              onTagsChange={handleTagsChange}
              isAuthenticated={isAuthenticated}
              onLandsChange={setLands}
              showSavedOnly={showSavedOnly}
              priceMin={priceRange.min}
              priceMax={priceRange.max}
            />
          } />

          {/* Login - Redirect if authenticated */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? 
              <Navigate to="/admin" replace /> : 
              <LoginPage onLogin={handleLogin} onGoogleLoginSuccess={markAuthenticated} />
            } 
          />
          
          {/* Admin Panel - Protected */}
          <Route 
            path="/admin" 
            element={
              isAuthenticated ? 
              <AdminPanelPage 
                onLogout={handleLogoutAndNavigate} 
                language={language} 
                toggleLanguage={toggleLanguage} 
              /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          {/* Add Land - Protected */}
          <Route 
            path="/admin/add-land" 
            element={
              isAuthenticated ? 
              <AddLandPage language={language} /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          {/* Edit Land - Protected */}
          <Route
            path="/admin/edit/:landId"
            element={
              isAuthenticated ?
              <AddLandPage language={language} /> :
              <Navigate to="/login" replace />
            }
          />

          {/* Add Tag - Protected */}
          <Route
            path="/admin/add-tag"
            element={
              isAuthenticated ?
              <AddTagPage language={language} /> :
              <Navigate to="/login" replace />
            }
          />

          {/* Edit Tag - Protected */}
          <Route
            path="/admin/edit-tag/:tagId"
            element={
              isAuthenticated ?
              <AddTagPage language={language} /> :
              <Navigate to="/login" replace />
            }
          />
          
          {/* Add User - Protected */}
          <Route 
            path="/admin/add-user" 
            element={
              isAuthenticated ? 
              <AddUserPage language={language} /> : 
              <Navigate to="/login" replace />
            } 
          />
          
          {/* Edit User - Protected */}
          <Route
            path="/admin/edit-user/:userId"
            element={
              isAuthenticated ?
              <AddUserPage language={language} /> :
              <Navigate to="/login" replace />
            }
          />
        </Routes>
        </Suspense>
      </div>
    </div>
    </GoogleOAuthProvider>
  );
}

export default App;
